import * as THREE from 'three';
import { logger } from './Logger';

export class LoadingManager {
  private manager: THREE.LoadingManager;
  private loadingScreen: HTMLDivElement;
  private totalItems: number = 0;
  private loadedItems: number = 0;
  private isLoading: boolean = false;
  private totalFiles: number = 0;
  private loadedFiles: number = 0;
  private onLoadingComplete: () => void;
  private isMobile: boolean;

  constructor(loadingScreen: HTMLDivElement, onLoadingComplete?: () => void) {
    this.loadingScreen = loadingScreen;
    this.isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    this.manager = new THREE.LoadingManager();
    
    this.manager.onStart = (url, itemsLoaded, itemsTotal) => {
      this.isLoading = true;
      this.totalItems = itemsTotal;
      logger.debug(`Started loading: ${url}`);
    };
    
    this.manager.onLoad = () => {
      logger.debug('Loading complete!');
      this.isLoading = false;
      // We don't hide the loading screen here anymore, it's handled in main.ts
    };
    
    this.manager.onProgress = (url, itemsLoaded, itemsTotal) => {
      this.loadedItems = itemsLoaded;
      this.totalItems = itemsTotal;
      logger.debug(`Loading file: ${url}. Loaded ${itemsLoaded}/${itemsTotal} files.`);
    };
    
    this.manager.onError = (url) => {
      logger.error(`Error loading: ${url}`);
      // Continue loading process even if one file fails
      this.loadedItems++;
      if (this.loadedItems >= this.totalItems) {
        this.manager.onLoad();
      }
    };

    // Ensure loading starts with at least one item
    this.totalItems = 1;

    this.onLoadingComplete = onLoadingComplete || (() => {});
  }
  
  getManager(): THREE.LoadingManager {
    return this.manager;
  }
  
  hideLoadingScreen(): void {
    // Always keep loading screen hidden on mobile
    if (this.isMobile) {
      this.loadingScreen.style.display = 'none';
      return;
    }
    
    // Normal fade out on desktop
    this.loadingScreen.style.opacity = '0';
    setTimeout(() => {
      this.loadingScreen.style.display = 'none';
    }, 500);
  }
  
  showLoadingScreen(): void {
    // Never show loading screen on mobile
    if (this.isMobile) {
      return;
    }
    
    this.loadingScreen.style.display = 'flex';
    this.loadingScreen.style.opacity = '1';
  }
  
  getProgress(): number {
    if (this.totalItems === 0) return 0;
    return this.loadedItems / this.totalItems;
  }

  isLoadingComplete(): boolean {
    return !this.isLoading;
  }

  startLoading(file: string) {
    this.totalFiles++;
    logger.debug(`Started loading: ${file}`);
  }

  fileLoaded(file: string) {
    this.loadedFiles++;
    logger.debug(`Loading file: ${file}. Loaded ${this.loadedFiles}/${this.totalFiles} files.`);

    if (this.loadedFiles === this.totalFiles) {
      logger.debug('Loading complete!');
      this.onLoadingComplete();
    }
  }

  reset() {
    this.totalFiles = 0;
    this.loadedFiles = 0;
  }
} 